import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Camera, Save, ArrowLeft, Loader2, ShieldCheck, FileText, CloudOff, RefreshCw, AlertTriangle } from "lucide-react";
import { Seo } from "@/components/Seo";
import { api } from "@/lib/api";

// OC1: the pinned inspection template is the SOLE authority for components, grouping and canonical
// angles. This screen renders purely from `template_keys` returned by the server — no hardcoded protocol.
const OPTS = [
  { v: "normal", label: "Normal", cls: "bg-green-500 text-white", ring: "ring-green-500" },
  { v: "attention", label: "Attention", cls: "bg-amber-400 text-navy-900", ring: "ring-amber-400" },
  { v: "urgent", label: "Urgent", cls: "bg-red-500 text-white", ring: "ring-red-500" },
  { v: "not_observed", label: "N/O", cls: "bg-slate-400 text-white", ring: "ring-slate-400" },
];
const GROUP_LABELS = { vessel: "Vessel", lift: "Lift", dock: "Dock" };
const lsKey = (id) => `oc1_draft_${id}`;

export default function OpsInspect() {
  const { visitId } = useParams();
  const navigate = useNavigate();
  const [insp, setInsp] = useState(null);
  const [keys, setKeys] = useState([]);
  const [obs, setObs] = useState({});
  const [notes, setNotes] = useState("");
  const [photos, setPhotos] = useState([]);        // {id, component_key, angle_key}
  const [queue, setQueue] = useState([]);           // pending uploads {tag, component_key, angle_key, dataUrl, status}
  const [saving, setSaving] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [completed, setCompleted] = useState(false);
  const [confirmGaps, setConfirmGaps] = useState(false);

  // ---- load / resume -------------------------------------------------------
  const init = useCallback(async () => {
    try {
      const { data } = await api.post(`/ops/visits/${visitId}/inspection`);
      setInsp(data);
      setKeys(data.template_keys || []);
      setCompleted(data.completion_status === "completed");
      // merge server draft with any locally cached unsynced edits
      let localObs = {}, localNotes = null, localQueue = [];
      try {
        const cached = JSON.parse(localStorage.getItem(lsKey(data.id)) || "{}");
        localObs = cached.obs || {}; localNotes = cached.notes ?? null; localQueue = cached.queue || [];
      } catch (_) { /* ignore */ }
      setObs({ ...(data.observations || {}), ...localObs });
      setNotes(localNotes != null ? localNotes : (data.notes || ""));
      setPhotos((data.photos || data.photo_ids || []).map((p) => (typeof p === "string" ? { id: p } : p)));
      setQueue(localQueue);
    } catch (e) {
      if (e.response?.status === 409) { toast.error("This inspection is already completed."); navigate("/ops"); }
      else toast.error("Could not open inspection.");
    }
  }, [visitId, navigate]);

  useEffect(() => { init(); }, [init]);

  useEffect(() => {
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener("online", on); window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  // persist a local draft for dockside resilience
  useEffect(() => {
    if (insp && !completed) localStorage.setItem(lsKey(insp.id), JSON.stringify({ obs, notes, queue }));
  }, [insp, obs, notes, queue, completed]);

  const keyMeta = (k) => keys.find((x) => x.key === k) || {};

  // ---- observations --------------------------------------------------------
  const setObsValue = (key, value) => {
    const next = { ...obs, [key]: { value, note: obs[key]?.note || "" } };
    setObs(next);
    saveDraft(next, notes);
  };
  const setNote = (key, note) => setObs((o) => ({ ...o, [key]: { value: o[key]?.value || "not_observed", note } }));

  const saveDraft = async (o = obs, n = notes, silent = true) => {
    if (!insp || completed) return;
    setSaving(true);
    try {
      await api.patch(`/ops/inspections/${insp.id}`, { observations: o, notes: n });
      if (!silent) toast.success("Progress saved.");
    } catch (e) { if (!silent) toast.error("Save failed — kept locally."); }
    finally { setSaving(false); }
  };

  // ---- photos: queue + retry-safe idempotent upload ------------------------
  const enqueuePhoto = (component_key, angle_key, file) => {
    const tag = `${insp.id}:${component_key}:${angle_key}:${Date.now()}`;
    const reader = new FileReader();
    reader.onload = () => {
      setQueue((q) => [...q, { tag, component_key, angle_key, dataUrl: reader.result, status: "pending" }]);
    };
    reader.readAsDataURL(file);
  };

  const uploadOne = useCallback(async (item) => {
    const blob = await (await fetch(item.dataUrl)).blob();
    const fd = new FormData();
    fd.append("file", blob, "photo.jpg");
    fd.append("client_tag", item.tag);               // idempotency: retries never duplicate
    if (item.component_key) fd.append("component_key", item.component_key);
    if (item.angle_key) fd.append("angle_key", item.angle_key);
    const { data } = await api.post(`/ops/inspections/${insp.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data;
  }, [insp]);

  // drain the queue whenever online and items are pending
  useEffect(() => {
    if (!insp || completed || !online) return;
    const pending = queue.find((q) => q.status === "pending");
    if (!pending) return;
    let cancelled = false;
    (async () => {
      setQueue((q) => q.map((x) => (x.tag === pending.tag ? { ...x, status: "uploading" } : x)));
      try {
        const res = await uploadOne(pending);
        if (cancelled) return;
        setPhotos((p) => (p.some((x) => x.id === res.id) ? p : [...p, { id: res.id, component_key: pending.component_key, angle_key: pending.angle_key }]));
        setQueue((q) => q.filter((x) => x.tag !== pending.tag));
      } catch (e) {
        if (cancelled) return;
        const s = e.response?.status;
        if (s === 400 || s === 415 || s === 413) {
          toast.error(s === 415 ? "Unsupported image." : s === 413 ? "Image too large (max 10MB)." : "Rejected by protocol.");
          setQueue((q) => q.filter((x) => x.tag !== pending.tag));   // permanent failure — drop
        } else {
          setQueue((q) => q.map((x) => (x.tag === pending.tag ? { ...x, status: "failed" } : x)));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [queue, online, insp, completed, uploadOne]);

  const retryFailed = () => setQueue((q) => q.map((x) => (x.status === "failed" ? { ...x, status: "pending" } : x)));
  const photosFor = (k) => photos.filter((p) => p.component_key === k);

  // ---- coverage accounting (client-side guidance only) ---------------------
  // states per component: complete-with-evidence | complete-missing-evidence | coverage-gap | not-recorded
  const componentState = (k) => {
    const meta = keyMeta(k.key);
    const val = obs[k.key]?.value;
    if (!val) return "not_recorded";
    if (val === "not_observed") return "coverage_gap";
    const expectsPhoto = meta.evidence_required || val === "attention" || val === "urgent";
    if (expectsPhoto && photosFor(k.key).length === 0) return "missing_evidence";
    return "complete";
  };
  const coverage = keys.reduce((acc, k) => {
    const st = componentState(k); acc[st] = (acc[st] || 0) + 1; return acc;
  }, {});
  const required = keys.filter((k) => keyMeta(k.key).observation_required);
  const unrecorded = required.filter((k) => !obs[k.key]?.value);
  const missingEvidence = keys.filter((k) => componentState(k) === "missing_evidence");
  const gapsPresent = unrecorded.length > 0 || missingEvidence.length > 0;

  const complete = async () => {
    if (gapsPresent && !confirmGaps) { setConfirmGaps(true); return; }
    await saveDraft(obs, notes, true);
    if (queue.length > 0) { toast.error("Photos still uploading — wait for the queue to finish."); return; }
    try {
      await api.post(`/ops/inspections/${insp.id}/complete`);
      setCompleted(true);
      localStorage.removeItem(lsKey(insp.id));
      toast.success("Inspection completed.");
    } catch (e) { toast.error("Could not complete inspection."); }
  };

  if (!insp) return <div className="min-h-screen flex items-center justify-center bg-navy-900"><Loader2 className="h-6 w-6 animate-spin text-gold-500" /></div>;

  const grouped = keys.reduce((g, k) => { (g[k.group] = g[k.group] || []).push(k); return g; }, {});
  const pendingCount = queue.filter((q) => q.status !== "failed").length;
  const failedCount = queue.filter((q) => q.status === "failed").length;

  return (
    <div className="min-h-screen bg-ivory dark:bg-navy-900 pb-28" data-testid="inspect-page">
      <Seo title="Dock Inspection — HMC" />
      <header className="bg-navy-900 sticky top-0 z-40 border-b border-gold-500/15">
        <div className="px-4 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/ops")} className="inline-flex items-center gap-1.5 text-slate-200 text-sm" data-testid="inspect-back"><ArrowLeft className="h-5 w-5" /> Visits</button>
          <span className="eyebrow text-gold-400 text-xs" data-testid="inspect-protocol">{insp.berth_type} · v{insp.template_version}</span>
          <span className="text-xs text-slate-400 w-10 text-right flex items-center justify-end gap-1">
            {!online && <CloudOff className="h-3.5 w-3.5 text-amber-400" />}{saving ? "…" : "✓"}
          </span>
        </div>
      </header>

      {completed && (
        <div className="bg-green-600 text-white px-4 py-3 text-sm flex items-center gap-2" data-testid="inspect-completed-banner">
          <Check className="h-4 w-4" /> Inspection completed — this record is now locked.
        </div>
      )}
      {!online && !completed && (
        <div className="bg-amber-100 text-amber-800 px-4 py-2 text-xs flex items-center gap-2" data-testid="inspect-offline-banner">
          <CloudOff className="h-3.5 w-3.5" /> Offline — your work is saved on this device and will sync automatically.
        </div>
      )}

      <div className="px-4 py-5 max-w-xl mx-auto">
        {/* coverage meter */}
        {!completed && (
          <div className="bg-white dark:bg-navy-800 rounded-2xl p-4 border border-gold-500/15 mb-4" data-testid="coverage-meter">
            <p className="font-medium text-navy-900 dark:text-white text-sm mb-2">Visit coverage</p>
            <div className="grid grid-cols-4 gap-2 text-center text-[11px]">
              <CovStat n={coverage.complete || 0} label="With evidence" cls="text-green-600" />
              <CovStat n={coverage.missing_evidence || 0} label="Photo missing" cls="text-amber-600" />
              <CovStat n={coverage.coverage_gap || 0} label="Coverage gap" cls="text-slate-500" />
              <CovStat n={coverage.not_recorded || 0} label="Not recorded" cls="text-red-500" />
            </div>
          </div>
        )}

        {Object.keys(grouped).map((group) => (
          <div key={group} className="mb-5" data-testid={`obs-group-${group}`}>
            <p className="eyebrow text-gold-600 dark:text-gold-400 text-xs mb-2">{GROUP_LABELS[group] || group}</p>
            <div className="space-y-3">
              {grouped[group].map((k) => (
                <ComponentCard
                  key={k.key} k={k} state={componentState(k)} value={obs[k.key]?.value} note={obs[k.key]?.note || ""}
                  photos={photosFor(k.key)} completed={completed}
                  onValue={(v) => setObsValue(k.key, v)} onNote={(n) => setNote(k.key, n)} onNoteBlur={() => saveDraft()}
                  onPhoto={(angle, file) => enqueuePhoto(k.key, angle, file)}
                />
              ))}
            </div>
          </div>
        ))}

        <div className="mt-2 bg-white dark:bg-navy-800 rounded-2xl p-4 border border-gold-500/15">
          <p className="font-medium text-navy-900 dark:text-white text-[15px] mb-2">Notes & actions taken</p>
          <textarea disabled={completed} value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={() => saveDraft()} rows={3}
            placeholder="Factual observations and any actions actually taken…" className="w-full bg-ivory dark:bg-navy-900 border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 text-sm text-navy-900 dark:text-white" data-testid="inspect-notes" />
        </div>

        {(pendingCount > 0 || failedCount > 0) && !completed && (
          <div className="mt-4 bg-white dark:bg-navy-800 rounded-2xl p-3 border border-gold-500/15 flex items-center justify-between text-sm" data-testid="upload-queue">
            <span className="text-slate-600 dark:text-slate-300">{pendingCount > 0 ? `${pendingCount} photo(s) uploading…` : ""}{failedCount > 0 ? ` ${failedCount} failed` : ""}</span>
            {failedCount > 0 && <button onClick={retryFailed} className="inline-flex items-center gap-1 text-gold-600" data-testid="retry-uploads"><RefreshCw className="h-4 w-4" /> Retry</button>}
          </div>
        )}

        <p className="mt-5 text-xs text-slate-500 dark:text-slate-400 flex items-start gap-2">
          <ShieldCheck className="h-4 w-4 shrink-0 text-gold-500" />
          Visual dockside observations only — not a marine survey, mechanical inspection, or seaworthiness determination. A missing observation or photo is an explicit coverage gap, never a finding of normal.
        </p>

        {completed && (
          <a href={`${api.defaults.baseURL}/ops/reports/${insp.id}/pdf`} target="_blank" rel="noreferrer"
            className="mt-5 inline-flex items-center gap-2 text-gold-600 dark:text-gold-400 text-sm font-semibold" data-testid="view-report-pdf">
            <FileText className="h-4 w-4" /> View owner report (PDF)
          </a>
        )}
      </div>

      {!completed && (
        <div className="fixed bottom-0 inset-x-0 bg-white dark:bg-navy-800 border-t border-gold-500/20 px-4 py-3 z-50">
          {confirmGaps && gapsPresent && (
            <div className="mb-2 text-xs text-amber-700 flex items-start gap-1.5" data-testid="gap-confirm">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {unrecorded.length} not recorded, {missingEvidence.length} missing expected photo. These will be logged as explicit coverage gaps. Tap Complete again to confirm.
            </div>
          )}
          <div className="flex items-center gap-3">
            <button onClick={() => saveDraft(obs, notes, false)} className="flex-1 inline-flex items-center justify-center gap-2 border border-navy-900/20 dark:border-white/15 text-navy-900 dark:text-white text-sm font-semibold py-3 rounded-full" data-testid="save-progress-btn">
              <Save className="h-4 w-4" /> Save
            </button>
            <button onClick={complete} className="flex-1 inline-flex items-center justify-center gap-2 bg-gold-500 hover:bg-gold-400 text-navy-900 text-sm font-semibold py-3 rounded-full" data-testid="complete-inspection-btn">
              <Check className="h-4 w-4" /> {gapsPresent ? "Complete with gaps" : "Complete"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const CovStat = ({ n, label, cls }) => (
  <div><p className={`text-xl font-bold ${cls}`}>{n}</p><p className="text-slate-400">{label}</p></div>
);

const STATE_BADGE = {
  complete: { t: "Evidence ✓", c: "text-green-600" },
  missing_evidence: { t: "Photo expected", c: "text-amber-600" },
  coverage_gap: { t: "Coverage gap", c: "text-slate-500" },
  not_recorded: { t: "Not recorded", c: "text-red-500" },
};

const ComponentCard = ({ k, state, value, note, photos, completed, onValue, onNote, onNoteBlur, onPhoto }) => {
  const fileRefs = useRef({});
  const angles = k.angles && k.angles.length ? k.angles : [{ key: "primary", label: "Photo" }];
  const expectsPhoto = k.evidence_required || value === "attention" || value === "urgent";
  const badge = STATE_BADGE[state];
  const angleHas = (ak) => photos.some((p) => p.angle_key === ak);
  return (
    <div className="bg-white dark:bg-navy-800 rounded-2xl p-4 border border-gold-500/15" data-testid={`obs-${k.key}`}>
      <div className="flex items-center justify-between mb-3">
        <p className="font-medium text-navy-900 dark:text-white text-[15px] pr-2">{k.label}{k.observation_required && <span className="text-red-400"> *</span>}</p>
        <span className={`text-[10px] font-semibold shrink-0 ${badge.c}`} data-testid={`obs-state-${k.key}`}>{badge.t}</span>
      </div>
      <div className="grid grid-cols-4 gap-2">
        {OPTS.map((o) => {
          const active = value === o.v;
          return (
            <button key={o.v} disabled={completed} onClick={() => onValue(o.v)}
              className={`h-12 rounded-xl text-sm font-semibold transition-all ${active ? o.cls + " ring-2 ring-offset-1 " + o.ring : "bg-slate-100 dark:bg-navy-900 text-slate-500 dark:text-slate-400"}`}
              data-testid={`obs-${k.key}-${o.v}`}>{o.label}</button>
          );
        })}
      </div>
      {(value === "attention" || value === "urgent") && (
        <input disabled={completed} value={note} onChange={(e) => onNote(e.target.value)} onBlur={onNoteBlur}
          placeholder="Add a short note…" className="mt-3 w-full bg-ivory dark:bg-navy-900 border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 text-sm text-navy-900 dark:text-white" data-testid={`obs-note-${k.key}`} />
      )}
      {!completed && value && value !== "not_observed" && (
        <div className="mt-3">
          <p className="text-[11px] text-slate-500 mb-1.5">{expectsPhoto ? "Expected photo evidence:" : "Optional photos:"}</p>
          <div className="flex flex-wrap gap-2">
            {angles.map((a) => (
              <label key={a.key} className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-full cursor-pointer border ${angleHas(a.key) ? "border-green-500/50 text-green-600 bg-green-500/5" : "border-gold-500/40 text-gold-600"}`} data-testid={`photo-angle-${k.key}-${a.key}`}>
                {angleHas(a.key) ? <Check className="h-3.5 w-3.5" /> : <Camera className="h-3.5 w-3.5" />} {a.label}
                <input ref={(el) => (fileRefs.current[a.key] = el)} type="file" accept="image/*" capture="environment" className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) onPhoto(a.key, f); if (fileRefs.current[a.key]) fileRefs.current[a.key].value = ""; }} />
              </label>
            ))}
          </div>
          {photos.length > 0 && <p className="mt-1.5 text-[10px] text-slate-400" data-testid={`photo-count-${k.key}`}>{photos.length} photo(s) captured</p>}
        </div>
      )}
    </div>
  );
};
